import { SwitchAccessShortcutAdd } from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemIcon,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material'
import React, { useState } from 'react'
import useAxiosPrivate from '../../../api/hooks/useAxiosPrivate'
import { TOrganisationListData } from '../../../utils/types/organisationsTypes'
import { useNavigate } from 'react-router-dom'
import useToastNotification from '../../../hooks/useToastNotification'
import { useAuthContext } from '../../../context/auth/AuthContextProvider'

type ChangeOrganisationPopupProps = {
  onClick: () => void
}

const ChangeOrganisationPopup = ({ onClick }: ChangeOrganisationPopupProps) => {
  const navigate = useNavigate()
  const { showApiErrorMessage, sendToastMessage } = useToastNotification()
  const { user, userSignOut } = useAuthContext()
  const axiosPrivate = useAxiosPrivate()
  const [openPopup, setOpenPopup] = useState<boolean>(false)
  const [selectedOrganisation, setSelectedOrganisation] = useState<string>(
    `${user.organisationId}`
  )
  const [organisationsData, setOrganisationsData] = useState<
    TOrganisationListData[] | []
  >([])

  const handleOrganisationChange = (event: SelectChangeEvent) => {
    setSelectedOrganisation(event.target.value)
  }

  const handleOpenPopup = async () => {
    onClick()

    if (organisationsData.length === 0) {
      await axiosPrivate
        .get('/api/organisations?pagination=false')
        .then((response) => {
          setOrganisationsData(response.data['hydra:member'])
          setOpenPopup(true)
        })
        .catch((error) => {
          setOpenPopup(false)

          showApiErrorMessage(
            error,
            'Something went wrong while fetching organisation list. Please refresh the page and try again.'
          )
        })
    } else {
      setOpenPopup(true)
    }
  }

  const handleComplete = async () => {
    await axiosPrivate
      .patch('/api/super-user/change-organisation', {
        organisationId: selectedOrganisation,
      })
      .then(() => {
        userSignOut()
        navigate('/')

        sendToastMessage(
          'success',
          'Organisation updated successfully. Please login back again.'
        )
      })
      .catch((error) => {
        showApiErrorMessage(
          error,
          'Something went wrong while saving organisation details. Please refresh the page and try again.'
        )
      })
  }

  return (
    <React.Fragment>
      <MenuItem onClick={handleOpenPopup}>
        <ListItemIcon>
          <SwitchAccessShortcutAdd fontSize="small" />
        </ListItemIcon>
        Switch Organisation
      </MenuItem>
      <Dialog open={openPopup} onClose={() => setOpenPopup(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Switch Organisation</DialogTitle>

        <DialogContent>
          <Box>
            <Box>
              To change organisation, please select the one you want to switch
              below.
            </Box>
            <Box mt={2}>
              <b>
                Be aware that if you decide to change organisation, you will be
                logged out so the change can take place.
              </b>
            </Box>
          </Box>
          <Box mt={3}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label" size="small">
                Organisation
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                size="small"
                label="Organisation"
                value={selectedOrganisation}
                onChange={handleOrganisationChange}
              >
                {organisationsData.map(
                  (organisation: TOrganisationListData) => {
                    return (
                      <MenuItem key={organisation.id} value={organisation.id}>
                        {organisation.name}
                      </MenuItem>
                    )
                  }
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ mr: 3, mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={() => setOpenPopup(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={handleComplete}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}

export default ChangeOrganisationPopup
